/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { Loading } from "notiflix/build/notiflix-loading-aio";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import ControlledTextInput from "components/FormInputs/ControlledTextInput";
import ControlledSelect from "components/FormInputs/ControlledSelect";
import MDButton from "components/MDButton";
import { globalCountry } from "store/entities/countries";
import { creditNotesSchema } from "../schemas/_model";
import { useLazyGetAllOrdersByCompanyQuery } from "services/orders";
import { useHistory } from "react-router-dom";
import { useApplyCreditnoteToOrderMutation } from "services/credit_note";
import { More } from "@mui/icons-material";
import { useChangeOrderPaymentStatusMutation } from "services/orders";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const ApplyCreditNoteDialog = ({ creditData }) => {
  const initialFormState = {
    order_id: 0,
    amount: 0,
  };
  const navigate = useHistory();
  const [openDialog, setOpenDialog] = useState(false);
  const [orders, setOrders] = useState([]);

  const selectedCountry = useSelector(globalCountry);

  const { control, watch, handleSubmit, setValue } = useForm({
    resolver: yupResolver(creditNotesSchema),
    defaultValues: initialFormState,
  });

  const orderId = watch("order_id");
  const amount = watch("amount");
  const availableCredits = creditData ? creditData.amount - (creditData.applied_amount ?? 0) : 0;

  const [triggerOrders] = useLazyGetAllOrdersByCompanyQuery();

  const [applyCreditnote] = useApplyCreditnoteToOrderMutation();
  const [changePaymentStatus] = useChangeOrderPaymentStatusMutation();

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    getOrders(creditData.reseller.company_id);
  }, [creditData]);

  // ⚡️ recalculate whenever order changes
  useEffect(() => {
    let order = orders.find((x) => x.id == orderId);
    if (order) {
      setValue("amount", order.grandtotal);
    }
  }, [orderId, setValue]);

  const getOrders = async (company_id) => {
    triggerOrders(company_id).then((orders) => {
      setOrders(orders.data);
    });
  };

  const onSubmit = (data) => {
    if (availableCredits < amount) return;
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });

    try {
      data.id = creditData.id;
      data.countryId = selectedCountry.id;
      data.reseller_id = creditData.reseller_id;
      data.company_id = creditData.reseller.companies.id;

      applyCreditnote(data).then((response) => {
        if (response.error) {
          Notify.failure(response.error.data.message);
        } else {
          changePaymentStatus({
            order_id: orderId,
            status: "PAID",
            invoiceNinjaAPI: selectedCountry?.invoiceNinjaAPI,
          }).then((response) => {
            if (response.data?.status == "success") {
              Notify.success("Succefully Updated!");
            }
          });
        }
      });
    } catch (e) {
      console.log(e);
    } finally {
      Loading.remove(500);
      handleClose();
      navigate.push(`/user/credit-notes`);
    }
  };

  return (
    <>
      <Tooltip title="Apply Creditnote to Order" placement="top">
        <IconButton aria-label="view" size="small" sx={{ ml: 1 }} onClick={handleClickOpen}>
          <More color="warning" fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <BootstrapDialog
        aria-labelledby="customized-dialog-title"
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
          },
        }}
        open={openDialog}
      >
        <DialogTitle sx={{ m: 0, p: 2, color: "#3f3f3f" }} id="customized-dialog-title">
          Apply Credit Note to an Order
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box
            sx={{
              fontWeight: 500,
              color: "#3f3f3f",
              mt: 3,
            }}
          >
            {availableCredits ? (
              <h4 id="blockDesc" className="mb-2">
                {" "}
                Available Credits: {availableCredits}
              </h4>
            ) : (
              <></>
            )}
            <form>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledSelect
                    name={"order_id"}
                    control={control}
                    label="Select an Order *"
                    menu={{
                      id: "id",
                      value: "invoice_number",
                      title: "Order",
                      list: orders,
                    }}
                    sx={{
                      "& .MuiFormLabel-root": {
                        backgroundColor: "#fff",
                      },
                      "& .MuiInputBase-root": {
                        height: "44px",
                      },
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledTextInput
                    name="amount"
                    control={control}
                    labelText="Amount *"
                    formControlProps={{
                      fullWidth: true,
                      type: "number",
                    }}
                  />
                </GridItem>
              </GridContainer>
            </form>
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton
            type="submit"
            color="primary"
            variant="gradient"
            id="submit"
            disabled={availableCredits < amount}
            onClick={handleSubmit(onSubmit)}
          >
            Sumbit
          </MDButton>
          <MDButton color="secondary" variant="gradient" autoFocus onClick={handleClose}>
            Cancel
          </MDButton>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

ApplyCreditNoteDialog.propTypes = {
  // children: PropTypes.node.isRequired, // You can also use PropTypes.element if you want to enforce a single element.
  creditData: PropTypes.object.isRequired,
};

export default ApplyCreditNoteDialog;
